"use client";

import React, { useEffect, useState } from "react";
import styles from "./system-status.module.scss";
import { HealthResponse } from "@/types/health.types";
import { HealthService } from "@/services/api/health.service";
import OverviewCard from "./overview-card/overview-card";
import ServicesStatus from "./services-status/services-status";
import BlockchainStatus from "./blockchain-status/blockchain-status";
import InfrastructureStatus from "./infrastructure-status/infrastructure-status";
import GatewaysStatus from "./gateways-status/gateways-status";
import LoadingIcon from "@/components/layout/loading-icon";

const SystemStatus: React.FC = () => {
  const [healthData, setHealthData] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchHealthData = async () => {
    try {
      setError(null);
      const data = await HealthService.getHealthStatus();
      setHealthData(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch health data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealthData();

    // Auto-refresh disabled - only manual refresh available
    // const interval = setInterval(fetchHealthData, 30000);
    // return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchHealthData();
  };

  if (loading && !healthData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>System Status</h1>
        </div>
        <div className={styles.loadingContainer}>
          <LoadingIcon />
          <p>Loading system status...</p>
        </div>
      </div>
    );
  }

  if (error && !healthData) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>System Status</h1>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <h3>Unable to load system status</h3>
            <p>{error}</p>
            <button className={styles.refreshButton} onClick={handleRefresh}>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!healthData) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1>System Status</h1>
          <p className={styles.subtitle}>
            Real-time monitoring of Blessnet infrastructure and services
          </p>
        </div>

        <div className={styles.headerActions}>
          <div className={styles.lastUpdated}>
            Last updated: {lastUpdated?.toLocaleTimeString() || "Never"}
          </div>
          <button
            className={styles.refreshButton}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      <div className={styles.content}>
        <div className={styles.topSection}>
          <OverviewCard healthData={healthData} />
        </div>

        <div className={styles.mainSection}>
          <div className={styles.leftColumn}>
            <ServicesStatus healthData={healthData} />
          </div>

          <div className={styles.rightColumn}>
            <BlockchainStatus healthData={healthData} />
            {/* Only show InfrastructureStatus if we have infrastructure data */}
            {(healthData.delivery?.available || healthData.dispenser?.available || healthData.sprinkler?.available || healthData.blessRouter?.available) && (
              <InfrastructureStatus healthData={healthData} />
            )}
            {/* Only show GatewaysStatus if we have gateway data */}
            {(healthData.tokenGateways?.available || healthData.transactionGateway?.available) && (
              <GatewaysStatus healthData={healthData} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemStatus;
